// workflows/delWorkflow/actions.js

import {
  deleteDel,
  transferDel,
  transferDelApproval,
  submitDel as apiSubmitDel,
  approveDel as apiApproveDel,
  rejectDel as apiRejectDel,
  unapproveDel as apiUnapproveDel,
  updateClientSignature,
  deleteClientSignature,
} from "../../../api/DelApi";
import { getLocalDateString } from "../../../utils/dateUtils";

export const makeDelWorkflowActions = (set, get) => ({
  deleteDraftDel: async (delId, userContext) => {
    const { delListsActions, notificationActions } = get();
    try {
      await deleteDel(delId, userContext);
      delListsActions.deleteDraftLocal(delId);
      delListsActions.setSelectedDraft(null);
      notificationActions.showNotification({
        type: "success",
        title: "DEL Deleted",
        message: "The DEL has been deleted successfully.",
      });
      return { ok: true };
    } catch (err) {
      console.error("Failed to delete DEL", err);
      notificationActions.showNotification({
        type: "error",
        title: "Error Deleting DEL",
        message: "Failed to delete DEL. Please try again later.",
      });
      return { ok: false, error: err };
    }
  },

  transferDel: async (delId, transEmpId, createdByEmpId, userContext) => {
    const { delListsActions, notificationActions } = get();
    try {
      await transferDel(delId, transEmpId, createdByEmpId, userContext);
      delListsActions.setSelectedDraft(null);
      delListsActions.deleteDraftLocal(delId);
      notificationActions.showNotification({
        type: "success",
        title: "DEL Transferred",
        message: "The DEL has been transferred successfully.",
      });
      return { ok: true };
    } catch (err) {
      console.error("Failed to transfer DEL", err);
      notificationActions.showNotification({
        type: "error",
        title: "Error Transferring DEL",
        message: "Failed to transfer DEL. Please try again later.",
      });
      return { ok: false, error: err };
    }
  },

  transferDelApproval: async (delId, employeeId) => {
    const { delListsActions, notificationActions } = get();
    try {
      await transferDelApproval(delId, employeeId);
      delListsActions.removeApprovalLocal(delId);
      delListsActions.setSelectedApproval(null);
      notificationActions.showNotification({
        type: "success",
        title: "DEL Transferred",
        message: "The DEL has been transferred successfully.",
      });
      return { ok: true };
    } catch (err) {
      console.error("Failed to transfer DEL approval", err);
      notificationActions.showNotification({
        type: "error",
        title: "Error Transferring DEL",
        message: "Failed to transfer DEL. Please try again later.",
      });
      return { ok: false, error: err };
    }
  },

  submitDel: async (delDataModel, delMode, userContext) => {
    const { notificationActions, delListsActions } = get();

    // Use simplified method - data already saved via autosave
    const payload = delDataModel.submitDelSimple(userContext);
    if (payload?.errors) {
      return {
        ok: false,
        delErrors: payload.errors,
        displayMessages: payload.displayMessages,
      };
    }

    try {
      const res = await apiSubmitDel(payload);
      if (res?.success) {
        // Build row for local list update
        const row = {
          id: delDataModel.id,
          del_creation_date:
            delDataModel.del_creation_date || getLocalDateString(),
          updated: getLocalDateString(),
          del_date: delDataModel.projectInfo.date || "",
          client_name: delDataModel.projectInfo.clientName || "",
          project_number: delDataModel.projectInfo.projectNumber || "-",
          project_name_description:
            delDataModel.projectInfo.projectDescription || "",
          project_name: delDataModel.projectInfo.projectName || "",
          phase_id: delDataModel.projectInfo.phaseId || "",
          task_id: delDataModel.projectInfo.taskId || "",
          project_manager_name:
            delDataModel.projectInfo.projectManager?.name || "",
          invoice_manager_name:
            delDataModel.projectInfo.invoiceManager?.name || "",
          del_submit_date: getLocalDateString(),
        };
        delListsActions.moveDraftToSubmitsLocal(row);
      }
      return { ok: !!res?.success };
    } catch (err) {
      console.error("submitDel error", err);
      // Handle backend validation errors
      if (err?.response?.data?.errors) {
        return { ok: false, delErrors: err.response.data.errors };
      }
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "A network error occurred while submitting your DEL. Please try again.";
      notificationActions.showNotification({
        type: "error",
        title: "Error Submitting DEL",
        message: msg,
      });
      return { ok: false, error: err };
    }
  },

  approveDel: async (delDataModel, delMode, userContext) => {
    const { notificationActions, delListsActions } = get();

    // Use simplified method - data already saved via autosave
    const payload = delDataModel.approveDelSimple(userContext);
    if (payload?.errors) {
      return {
        ok: false,
        delErrors: payload.errors,
        displayMessages: payload.displayMessages,
      };
    }

    try {
      const res = await apiApproveDel(payload);
      if (res?.success) {
        // Move from submits to approved
        const row = {
          id: delDataModel.id,
          del_creation_date: delDataModel.del_creation_date || "",
          updated: getLocalDateString(),
          del_date: delDataModel.projectInfo.date || "",
          client_name: delDataModel.projectInfo.clientName || "",
          project_number: delDataModel.projectInfo.projectNumber || "-",
          project_name_description:
            delDataModel.projectInfo.projectDescription || "",
          project_name: delDataModel.projectInfo.projectName || "",
          phase_id: delDataModel.projectInfo.phaseId || "",
          task_id: delDataModel.projectInfo.taskId || "",
          project_manager_name:
            delDataModel.projectInfo.projectManager?.name || "",
          invoice_manager_name:
            delDataModel.projectInfo.invoiceManager?.name || "",
          del_submit_date: delDataModel.del_submit_date || "",
          del_approval_date: getLocalDateString(),
          approved: true,
        };
        // Remove from submits.items locally and push into approved.items
        set((s) => {
          const submitsItems = s.delLists.submits.items.filter(
            (d) => d.id !== row.id,
          );
          const approvedItems = [row, ...s.delLists.approved.items];
          const approvedCount = (s.delLists.approved.totalCount || 0) + 1;
          return {
            delLists: {
              ...s.delLists,
              submits: { ...s.delLists.submits, items: submitsItems },
              approved: {
                ...s.delLists.approved,
                items: approvedItems,
                totalCount: approvedCount,
              },
            },
          };
        });
      }
      return { ok: !!res?.success };
    } catch (err) {
      console.error("approveDel error", err);
      // Handle backend validation errors
      if (err?.response?.data?.errors) {
        return { ok: false, delErrors: err.response.data.errors };
      }
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "A network error occurred while approving your DEL. Please try again.";
      notificationActions.showNotification({
        type: "error",
        title: "Error Approving DEL",
        message: msg,
      });
      return { ok: false, error: err };
    }
  },

  rejectDel: async (delId, rejectedReason, userContext) => {
    const { notificationActions } = get();
    try {
      const res = await apiRejectDel(delId, rejectedReason, userContext);
      if (!res?.success)
        return { ok: false, error: res?.error || "Reject failed" };
      return { ok: true };
    } catch (err) {
      console.error("rejectDel error", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "A network error occurred while rejecting your DEL. Please try again.";
      notificationActions.showNotification({
        type: "error",
        title: "Error Rejecting DEL",
        message: msg,
      });
      return { ok: false, error: err };
    }
  },

  unapproveDel: async (delId, userContext) => {
    const { notificationActions } = get();
    try {
      const res = await apiUnapproveDel(delId, userContext);
      // console.log("res", res);
      if (!res?.success)
        return { ok: false, error: res?.error || "Unapproving failed" };
      return { ok: true };
    } catch (err) {
      console.error("unapproveDel error", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "A network error occurred while unapproving your DEL. Please try again.";
      notificationActions.showNotification({
        type: "error",
        title: "Error Unapproving DEL",
        message: msg,
      });
      return { ok: false, error: err };
    }
  },
});
